import { WordSense, validateWordSenses } from '@lingoping/core';
import { getSupabase, isSupabaseConfigured } from '../config/supabase.js';
import { platform } from '../platform.js';
import { StorageManager } from '../storage/index.js';
import { throwIfAccessLocked } from './access.js';

/** A word's separate meanings, and the one its sentence points at. */
export interface WordMeanings {
  senses: WordSense[];
  suggested?: string;
}

/** The server accepts this many words per request. */
const CHUNK = 200;

/**
 * Which of many words have SEPARATE meanings (Docs/plans/WORD_SENSES_PLAN.md), from the
 * `word-senses` Edge Function. Lower-cased word -> its meanings; a word that is missing has one
 * meaning or nothing is known about it. Empty when meanings are off here (the website with an
 * older extension), signed out, offline or on a server error: the import then works as before.
 * Throws AccessLockedError when the learner has no access (services/access.ts).
 */
export async function meaningsOfWords(
  words: Array<{ lemma: string; context?: string | null }>,
  language = 'de'
): Promise<Map<string, WordMeanings>> {
  const found = new Map<string, WordMeanings>();
  if (words.length === 0 || !(await platform.sensesEnabled())) return found;

  // Development with the simulated sign-in has no server: a few sample words stand in.
  if (import.meta.env.DEV && (await StorageManager.getCurrentUserId())?.startsWith('dev-')) {
    const { devMeaningsOf } = await import('../dev/devSenses.js');
    for (const w of words) {
      const senses = devMeaningsOf(w.lemma);
      if (senses) found.set(w.lemma.trim().toLowerCase(), { senses });
    }
    return found;
  }

  const profile = await StorageManager.getProfile();
  const key = profile.supabase_publishable_key || profile.supabase_anon_key;
  if (!isSupabaseConfigured(profile.supabase_url, key)) return found;
  const supabase = getSupabase(profile.supabase_url, key);
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return found;

  for (let start = 0; start < words.length; start += CHUNK) {
    const chunk = words.slice(start, start + CHUNK);
    const { data, error } = await supabase.functions.invoke<{
      results?: Array<{ lemma?: unknown; senses?: unknown; suggested?: unknown }>;
    }>('word-senses', {
      body: {
        language,
        words: chunk.map((w) => ({
          lemma: w.lemma,
          context: w.context?.slice(0, 300) || undefined,
        })),
      },
    });
    if (error) await throwIfAccessLocked(error);
    if (error || !Array.isArray(data?.results)) {
      console.warn('[LingoPing] Meanings of the imported words unavailable:', error?.message);
      return found;
    }
    data.results.forEach((r, i) => {
      const senses = validateWordSenses(r?.senses);
      if (senses.length < 2) return;
      const suggested =
        typeof r?.suggested === 'string' && senses.some((s) => s.sense_key === r.suggested)
          ? r.suggested
          : undefined;
      found.set(chunk[i].lemma.trim().toLowerCase(), { senses, suggested });
    });
  }
  return found;
}
